import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';

type ThemePreference = 'light' | 'dark' | 'pastel' | 'system';
type ResolvedTheme = 'light' | 'dark' | 'pastel';
export type PastelVariant = 'claire' | 'soft' | 'earth' | 'sausalito';

interface ThemeContextType {
  preference: ThemePreference;
  theme: ResolvedTheme;
  setPreference: (pref: ThemePreference) => void;
  pastelVariant: PastelVariant;
  setPastelVariant: (v: PastelVariant) => void;
}

const STORAGE_KEY = 'theme-preference';
const PASTEL_VARIANT_KEY = 'pastel-variant';

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export const useTheme = (): ThemeContextType => {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error('useTheme must be used within ThemeProvider');
  return ctx;
};

function getSystemTheme(): ResolvedTheme {
  if (typeof window === 'undefined') return 'light';
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

function resolveTheme(pref: ThemePreference): ResolvedTheme {
  if (pref === 'system') return getSystemTheme();
  return pref;
}

export const ThemeProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [preference, setPreferenceState] = useState<ThemePreference>(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved === 'light' || saved === 'dark' || saved === 'pastel' || saved === 'system') return saved;
    return 'system';
  });

  const [pastelVariant, setPastelVariantState] = useState<PastelVariant>(() => {
    const saved = localStorage.getItem(PASTEL_VARIANT_KEY);
    if (saved === 'claire' || saved === 'soft' || saved === 'earth' || saved === 'sausalito') return saved;
    return 'soft';
  });

  const [theme, setTheme] = useState<ResolvedTheme>(() => resolveTheme(preference));

  // Apply data-theme and data-pastel attributes to <html>
  useEffect(() => {
    const resolved = resolveTheme(preference);
    setTheme(resolved);
    document.documentElement.setAttribute('data-theme', resolved);
    if (resolved === 'pastel') {
      document.documentElement.setAttribute('data-pastel', pastelVariant);
    } else {
      document.documentElement.removeAttribute('data-pastel');
    }
  }, [preference, pastelVariant]);

  // Listen for system theme changes when preference is 'system'
  useEffect(() => {
    if (preference !== 'system') return;
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const handler = (e: MediaQueryListEvent) => {
      const resolved = e.matches ? 'dark' : 'light';
      setTheme(resolved);
      document.documentElement.setAttribute('data-theme', resolved);
      document.documentElement.removeAttribute('data-pastel');
    };
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, [preference]);

  const setPreference = useCallback((pref: ThemePreference) => {
    setPreferenceState(pref);
    localStorage.setItem(STORAGE_KEY, pref);
  }, []);

  const setPastelVariant = useCallback((v: PastelVariant) => {
    setPastelVariantState(v);
    localStorage.setItem(PASTEL_VARIANT_KEY, v);
  }, []);

  return (
    <ThemeContext.Provider value={{ preference, theme, setPreference, pastelVariant, setPastelVariant }}>
      {children}
    </ThemeContext.Provider>
  );
};
